import { useCallback, useState } from "react";
import { debounce } from "lodash";
import { DSBaseTable, DSTableCell } from "../../system-design/Table";
import { Column } from "../../system-design/Table/DSBaseTable/components/DSBaseTable";
import DSBox from "../../system-design/DSBox";
import DSTypography from "../../system-design/DSTypography";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import PageHeader from "components/PageHeader";
import FilterBar from "components/FilterBar";
import { CrashBundle } from "../../../api/crashBundles";
import useCrashBundlesQuery from "../hooks/useCrashBundlesQuery";
import { useRequestBundleMutation } from "../hooks/useCrashBundleMutations";
import RunAnalysisModal from "./RunAnalysisModal";

function ClockCrashes() {
  const [searchInput, setSearchInput] = useState<string>("");
  const [clockIdSearch, setClockIdSearch] = useState<string>("");
  const [selectedBundle, setSelectedBundle] = useState<CrashBundle | null>(
    null,
  );
  const [showModal, setShowModal] = useState(false);

  const debouncedSearch = useCallback(
    debounce((s: string) => setClockIdSearch(s.trim()), 500),
    [],
  );

  const { data: bundles, isLoading } = useCrashBundlesQuery(clockIdSearch);
  const requestBundle = useRequestBundleMutation();

  const columns: Column<CrashBundle>[] = [
    { label: "Clock ID", field: "clockId" },
    { label: "Collected", field: "timestamp" },
    { label: "File", field: "fileName" },
    { label: "Size", field: "sizeBytes" },
    { label: "Actions", field: "fileKey" },
  ];

  const rowBuilder = (bundle: CrashBundle, cols: Column<CrashBundle>[]) => (
    <>
      {cols.map((col) => {
        if (col.field === "clockId") {
          return <DSTableCell key="clockId">{bundle.clockId}</DSTableCell>;
        }
        if (col.field === "timestamp") {
          return (
            <DSTableCell key="timestamp">
              {bundle.timestamp
                ? new Date(bundle.timestamp).toLocaleString()
                : new Date(bundle.createdAt).toLocaleString()}
            </DSTableCell>
          );
        }
        if (col.field === "fileName") {
          return <DSTableCell key="fileName">{bundle.fileName}</DSTableCell>;
        }
        if (col.field === "sizeBytes") {
          return (
            <DSTableCell key="sizeBytes">
              {Math.max(1, Math.round(bundle.sizeBytes / 1024))} KB
            </DSTableCell>
          );
        }
        return (
          <DSTableCell key="actions">
            <DSPrimaryButton
              size="small"
              onClick={() => {
                setSelectedBundle(bundle);
                setShowModal(true);
              }}
            >
              Run analysis
            </DSPrimaryButton>
          </DSTableCell>
        );
      })}
    </>
  );

  return (
    <>
      <PageHeader
        overline="Devices"
        title="Clock Crashes"
        subtitle="Browse uploaded crash bundles, request new ones, and run AI analysis."
      />
      <FilterBar
        search={{
          value: searchInput,
          onChange: (value: string) => {
            setSearchInput(value);
            debouncedSearch(value);
          },
          placeholder: "Search by clock ID…",
        }}
      />

      <DSBox
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        <DSPrimaryButton
          disabled={!searchInput.trim() || requestBundle.isPending}
          onClick={() => requestBundle.mutate(searchInput.trim())}
        >
          Request bundle from this clock
        </DSPrimaryButton>
        {requestBundle.isPending && <DSCircularProgress size={20} />}
        {requestBundle.isSuccess && (
          <DSTypography color="success.main">
            Requested — the clock will upload its crash bundle shortly.
          </DSTypography>
        )}
        {requestBundle.isError && (
          <DSTypography color="error">Failed to request bundle.</DSTypography>
        )}
      </DSBox>

      {isLoading ? (
        <DSBox sx={{ textAlign: "center", p: "50px" }}>
          <DSCircularProgress />
        </DSBox>
      ) : bundles && bundles.length > 0 ? (
        <DSBaseTable
          data={bundles}
          columns={columns}
          rowBuilder={rowBuilder}
          getRowId={(b) => b.fileKey}
        />
      ) : (
        <DSBox sx={{ textAlign: "center", p: "50px" }}>
          <DSTypography>No crash bundles found.</DSTypography>
        </DSBox>
      )}

      <RunAnalysisModal
        show={showModal}
        bundle={selectedBundle}
        onClose={() => {
          setShowModal(false);
          setSelectedBundle(null);
        }}
      />
    </>
  );
}

export default ClockCrashes;
